import React, { ChangeEvent } from "react";

import {
  Alert,
  Box,
  Button,
  Column,
  Columns,
  Stack,
  Text,
  TextField,
} from "@origin-digital/ods-core";
import { Autocomplete } from "@origin-digital/ods-lab";

import { content } from "./content";
import { reduceValidators } from "./helpers";
import { useAutocompleteState, useTextFieldState } from "./hooks";
import { metadata } from "./metadata";
import { AddressManualEntryField, AddressManualEntryValues } from "./models";
import { State, UnitType, stateOptions, unitTypeOptions } from "./options";
import { call, createSafeLabel, getOptionLabel } from "../helpers";

export interface AddressManualEntryProps {
  onSubmit: (values: AddressManualEntryValues, errors: string[]) => void;
  onChange?: (
    field: AddressManualEntryField,
    value: string,
    event: ChangeEvent<HTMLInputElement>
  ) => void;
  id: string;
  "data-id"?: string;
  loading?: boolean;
  disabled?: boolean;
  errorMessage?: string;
  showAddressButton?: boolean;
  manualEntryValues: AddressManualEntryValues;
}

export const AddressManualEntry = ({
  onSubmit,
  onChange,
  disabled,
  loading,
  id,
  errorMessage,
  showAddressButton = true,
  manualEntryValues,
  ...props
}: AddressManualEntryProps) => {
  const {
    unitType = undefined,
    unitNumber = "",
    streetNumber = "",
    streetName = "",
    city = "",
    state = "",
    postcode = "",
  } = manualEntryValues;

  const fieldStates: Record<AddressManualEntryField, any> = {
    unitType: useAutocompleteState<UnitType>(unitType),
    unitNumber: useTextFieldState(unitNumber),
    streetNumber: useTextFieldState(streetNumber),
    streetName: useTextFieldState(streetName),
    city: useTextFieldState(city),
    state: useAutocompleteState<State>(state),
    postcode: useTextFieldState(postcode),
  };

  const dataId = props["data-id"] || id;

  const getValues = (): AddressManualEntryValues => {
    const values: AddressManualEntryValues = { ...fieldStates };

    for (const key in fieldStates) {
      if (fieldStates[key]) {
        const field = key as AddressManualEntryField;
        values[field] =
          field === "unitType"
            ? fieldStates[field].value
              ? fieldStates[field].value.value
              : ""
            : fieldStates[field].value;
      }
    }

    return values;
  };

  const handleValidate = (field: AddressManualEntryField) => () => {
    const { validators } = metadata[field];
    const { value } = fieldStates[field];
    const error = reduceValidators(value, getValues(), validators as any);

    fieldStates[field].setError(error);

    return error;
  };

  const handleChange =
    (field: AddressManualEntryField) =>
    (event: ChangeEvent<HTMLInputElement>, newValue?: string) => {
      fieldStates[field].onChange(event, newValue);
      call(onChange, field, newValue || event.target.value, event);
    };

  const getBaseProps = (field: AddressManualEntryField) => {
    const { label } = metadata[field];

    return {
      disabled: disabled || loading,
      label: createSafeLabel(label),
      id: `${id}-${field}`,
      "data-id": `${dataId}-${field}`,
      ...fieldStates[field],
      onBlur: handleValidate(field),
      onChange: handleChange(field),
      domProps: {
        autoComplete: "disable",
      },
    };
  };

  const getAutocompleteProps = (field: AddressManualEntryField) => {
    return {
      muiProps: {
        autoHighlight: true,
        debug: true,
      },
      textFieldProps: {
        "data-id": `${dataId}-${field}-text-field`,
      },
    };
  };

  const handleSubmit = () => {
    const errors = [];
    const values = getValues();

    for (const key in fieldStates) {
      if (fieldStates[key]) {
        const field = key as AddressManualEntryField;
        const error = handleValidate(field)();

        if (error) {
          errors.push({
            field,
            error,
          });
        }
      }
    }

    call(onSubmit, values, errors);
  };

  return (
    <Box id={id} data-id={dataId}>
      <Stack>
        <Columns collapseBelow="sm" space={["medium", "large"]}>
          <Column width="2/3">
            <Columns>
              <Column width="1/2">
                <Autocomplete<UnitType>
                  options={unitTypeOptions}
                  getOptionLabel={getOptionLabel}
                  {...getBaseProps("unitType")}
                  {...getAutocompleteProps("unitType")}
                />
              </Column>
              <Column width="1/2">
                <TextField {...getBaseProps("unitNumber")} />
              </Column>
            </Columns>
          </Column>

          <Column width="1/3">
            <TextField {...getBaseProps("streetNumber")} />
          </Column>
        </Columns>

        <TextField {...getBaseProps("streetName")} />

        <Columns collapseBelow="sm">
          <Column width="1/3">
            <TextField {...getBaseProps("city")} />
          </Column>

          <Column width="2/3">
            <Columns>
              <Column width="1/2">
                <Autocomplete<State>
                  options={stateOptions}
                  {...getBaseProps("state")}
                  {...getAutocompleteProps("state")}
                />
              </Column>
              <Column width="1/2">
                <TextField {...getBaseProps("postcode")} maxLength={4} />
              </Column>
            </Columns>
          </Column>
        </Columns>

        {errorMessage && (
          <Alert tone="critical" data-id="address-error">
            <Text>{errorMessage}</Text>
          </Alert>
        )}

        {showAddressButton && (
          <Button
            disabled={disabled}
            data-id={`${dataId}-button`}
            onClick={handleSubmit}
            spinner={loading}
          >
            {content.button.label}
          </Button>
        )}
      </Stack>
    </Box>
  );
};

AddressManualEntry.defaultProps = {};
